import { memo, useEffect, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  addDays,
  addMonths,
  addYears,
  buildMonthGrid,
  type Civil,
  compareDate,
  isSameDay,
  monthNames,
  orderedWeekdays,
  startOfWeek,
  weekdayNames,
} from './date-utils';

interface CalendarProps {
  viewYear: number;
  viewMonth: number; // 1–12
  /** Selection endpoints in civil form. For single modes only `selStart` is set. */
  selStart: Civil | null;
  selEnd: Civil | null;
  /** Live hover target for range preview (or week preview). */
  hover: Civil | null;
  focus: Civil;
  today: Civil;
  isRange: boolean;
  weekMode: boolean;
  firstDayOfWeek: number;
  locale: string;
  dir: 'ltr' | 'rtl';
  isDisabled: (d: Civil) => boolean;
  isHoliday: (d: Civil) => boolean;
  onSelect: (d: Civil) => void;
  onHover: (d: Civil | null) => void;
  /** Move focus (and, when the target month differs, the visible month). */
  onFocusChange: (d: Civil) => void;
}

/**
 * A single month grid. Owns roving-tabindex keyboard navigation over its 42
 * cells; selection, range-preview shading, and disabled styling are derived from
 * civil comparisons so nothing here touches timezones.
 */
export const Calendar = memo(function Calendar({
  viewYear,
  viewMonth,
  selStart,
  selEnd,
  hover,
  focus,
  today,
  isRange,
  weekMode,
  firstDayOfWeek,
  locale,
  dir,
  isDisabled,
  isHoliday,
  onSelect,
  onHover,
  onFocusChange,
}: CalendarProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const cells = buildMonthGrid(viewYear, viewMonth, firstDayOfWeek);
  const dow = orderedWeekdays(firstDayOfWeek);
  const names = weekdayNames(locale, 'short');
  // Each grid labels its own month: the shared header sits above *both*
  // calendars in a two-month range, so it can't say which is which.
  const months = useMemo(() => monthNames(locale, 'long'), [locale]);
  const caption = `${months[viewMonth - 1]} ${viewYear}`;

  // When focus changes via keyboard, move DOM focus to the new cell — but only
  // if the grid already holds focus, so opening the popup doesn't yank focus.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || !grid.contains(document.activeElement)) return;
    grid.querySelector<HTMLElement>('[data-focused="true"]')?.focus();
  }, [focus]);

  // Range preview: when only a start is chosen, shade toward the hovered day.
  const rangeEnd = selEnd ?? (isRange && selStart ? hover : null);
  const lo = selStart && rangeEnd ? (compareDate(selStart, rangeEnd) <= 0 ? selStart : rangeEnd) : null;
  const hi = selStart && rangeEnd ? (compareDate(selStart, rangeEnd) <= 0 ? rangeEnd : selStart) : null;

  // Week-mode preview: the whole week containing hover (or focus) highlights.
  const weekAnchor = weekMode ? (hover ?? focus) : null;
  const weekStart = weekAnchor ? startOfWeek(weekAnchor, firstDayOfWeek) : null;
  const weekEnd = weekStart ? addDays(weekStart, 6) : null;

  function inWeek(d: Civil): boolean {
    if (!weekStart || !weekEnd) return false;
    return compareDate(d, weekStart) >= 0 && compareDate(d, weekEnd) <= 0;
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    const ltr = dir !== 'rtl';
    let next: Civil | null = null;
    switch (e.key) {
      case 'ArrowLeft':
        next = addDays(focus, ltr ? -1 : 1);
        break;
      case 'ArrowRight':
        next = addDays(focus, ltr ? 1 : -1);
        break;
      case 'ArrowUp':
        next = addDays(focus, -7);
        break;
      case 'ArrowDown':
        next = addDays(focus, 7);
        break;
      case 'Home':
        next = startOfWeek(focus, firstDayOfWeek);
        break;
      case 'End':
        next = addDays(startOfWeek(focus, firstDayOfWeek), 6);
        break;
      case 'PageUp':
        next = e.shiftKey ? addYears(focus, -1) : addMonths(focus, -1);
        break;
      case 'PageDown':
        next = e.shiftKey ? addYears(focus, 1) : addMonths(focus, 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isDisabled(focus)) onSelect(focus);
        return;
      default:
        return;
    }
    e.preventDefault();
    if (next) onFocusChange(next);
  }

  return (
    <div>
      <div className="mb-1 text-center text-xs font-semibold text-foreground">{caption}</div>
      <div className="mb-1 grid grid-cols-7" aria-hidden="true">
        {dow.map((d) => (
          <div key={d} className="flex h-8 items-center justify-center text-[10px] font-medium text-muted-foreground">
            {names[d]}
          </div>
        ))}
      </div>
      <div
        ref={gridRef}
        role="grid"
        aria-label={caption}
        className="grid grid-cols-7 gap-y-0.5"
        onKeyDown={handleKeyDown}
        onMouseLeave={() => onHover(null)}
      >
        {cells.map(({ date, inCurrentMonth }) => {
          const disabled = isDisabled(date);
          const isStart = lo != null && isSameDay(date, lo);
          const isEnd = hi != null && isSameDay(date, hi);
          const isEndpoint = isStart || isEnd || (!isRange && selStart != null && isSameDay(date, selStart));
          const between = lo != null && hi != null && compareDate(date, lo) > 0 && compareDate(date, hi) < 0;
          const isToday = isSameDay(date, today);
          const isFocused = isSameDay(date, focus);
          const holiday = isHoliday(date);
          const weekHi = inWeek(date);

          return (
            <div key={`${date.year}-${date.month}-${date.day}`} role="gridcell" className="flex justify-center">
              <button
                type="button"
                tabIndex={isFocused ? 0 : -1}
                data-focused={isFocused || undefined}
                disabled={disabled}
                aria-disabled={disabled || undefined}
                aria-selected={isEndpoint || undefined}
                aria-current={isToday ? 'date' : undefined}
                onClick={() => onSelect(date)}
                onMouseEnter={() => onHover(date)}
                onFocus={() => onHover(date)}
                className={cn(
                  'relative flex h-9 w-9 items-center justify-center rounded-lg text-xs transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                  !inCurrentMonth && 'text-muted-foreground/50',
                  inCurrentMonth && !isEndpoint && 'text-foreground',
                  // Range fill (between endpoints) + week preview.
                  (between || weekHi) && !isEndpoint && 'rounded-none bg-primary/10 text-foreground',
                  isStart && isRange && hi != null && !isEnd && 'rounded-r-none',
                  isEnd && isRange && lo != null && !isStart && 'rounded-l-none',
                  // Endpoints / single selection.
                  isEndpoint && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
                  // Today ring when not selected.
                  isToday && !isEndpoint && 'font-semibold text-primary ring-1 ring-inset ring-primary/40',
                  // Hover (non-selected, enabled).
                  !isEndpoint && !between && !weekHi && !disabled && 'hover:bg-muted',
                  disabled && 'cursor-not-allowed text-muted-foreground/40 line-through',
                )}
              >
                {date.day}
                {holiday && !isEndpoint && (
                  <span className="absolute bottom-1 h-1 w-1 rounded-full bg-destructive" aria-hidden="true" />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
});
